// src/components/Countries/EditCountryModal.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { updateCountry } from "../../redux/slices/countriesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useTranslation } from "../../contexts/LanguageContext";

const EditCountryModal = ({ isOpen, setIsOpen, country }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // List of countries fetched from REST Countries API
  const [countriesList, setCountriesList] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Form state with all fields
  const [formData, setFormData] = useState({
    countryFlagImage: "",
    countryCode: "",
    countryName: "",
    currency: "",
    localCurrencySymbol: "",
    globalCurrencySymbol: "",
    defaultLanguage: "",
    taxRate: "",
    timezone: "",
    mobileCountryCode: "",
    isActive: true,
    createdBy: "",
    updatedBy: ""
  });

  // Fetch countries list from REST Countries API when modal opens
  useEffect(() => {
    const fetchCountriesList = async () => {
      setLoadingCountries(true);
      try {
        const response = await axios.get(
          "https://restcountries.com/v3.1/all?fields=name,cca2,flags,currencies,languages,timezones,idd",
          { timeout: 10000 }
        );
        const sortedCountries = response.data.sort((a, b) =>
          a.name.common.localeCompare(b.name.common)
        );
        setCountriesList(sortedCountries);
      } catch (error) {
        console.error("Error fetching countries list:", error.message);
        dispatch(
          showNotification({
            message: t("country.msg.failedLoadList"),
            type: "error"
          })
        );
      } finally {
        setLoadingCountries(false);
      }
    };
    if (isOpen) {
      fetchCountriesList();
    }
  }, [isOpen, dispatch, t]);

  // Pre-fill form fields with the country's current values when editing
  useEffect(() => {
    if (country) {
      setFormData({
        countryFlagImage: country.countryFlagImage || "",
        countryCode: country.countryCode || "",
        countryName: country.countryName || "",
        currency: country.currency || "",
        localCurrencySymbol: country.localCurrencySymbol || "",
        globalCurrencySymbol: country.globalCurrencySymbol || "",
        defaultLanguage: country.defaultLanguage || "",
        taxRate: country.taxRate || "",
        timezone: country.timezone || "",
        mobileCountryCode: country.mobileCountryCode || "",
        isActive: country.isActive,
        createdBy: country.createdBy || "",
        updatedBy: country.updatedBy || ""
      });
    }
  }, [country]);

  // Common change handler
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "taxRate"
          ? parseFloat(value)
          : value
    }));
  };

  // When a country is selected from the dropdown, autofill values using REST Countries API data
  const handleCountrySelect = (e) => {
    const selectedCountryCode = e.target.value;
    const selectedCountry = countriesList.find(
      (c) => c.cca2 === selectedCountryCode
    );
    if (selectedCountry) {
      // Auto-fill fields
      const flagImage = selectedCountry.flags?.png || "";
      const countryCode = selectedCountry.cca2 || "";
      const countryName = selectedCountry.name?.common || "";

      // For currencies, take the first key if available
      let currency = "";
      let localCurrencySymbol = "";
      let globalCurrencySymbol = "";
      if (selectedCountry.currencies) {
        const currencyKeys = Object.keys(selectedCountry.currencies);
        if (currencyKeys.length > 0) {
          currency = currencyKeys[0];
          localCurrencySymbol = selectedCountry.currencies[currency]?.symbol || "";
          globalCurrencySymbol = currency; // default to currency code
        }
      }

      // Default language: take first value if available
      let defaultLanguage = "";
      if (selectedCountry.languages) {
        const languageKeys = Object.keys(selectedCountry.languages);
        if (languageKeys.length > 0) {
          defaultLanguage = selectedCountry.languages[languageKeys[0]];
        }
      }

      // Timezone: first value from timezones array
      const timezone = selectedCountry.timezones?.[0] || "";

      // Mobile country code: combine idd.root and first suffix if available
      let mobileCountryCode = "";
      if (selectedCountry.idd) {
        mobileCountryCode =
          selectedCountry.idd.root +
          (selectedCountry.idd.suffixes && selectedCountry.idd.suffixes.length > 0
            ? selectedCountry.idd.suffixes[0]
            : "");
      }

      setFormData((prev) => ({
        ...prev,
        countryFlagImage: flagImage,
        countryCode,
        countryName,
        currency,
        localCurrencySymbol,
        globalCurrencySymbol,
        defaultLanguage,
        timezone,
        mobileCountryCode
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateCountry({ countryId: country._id, countryData: formData })).unwrap();
      dispatch(showNotification({ message: t("country.msg.updatedSuccess"), type: "success" }));
      setIsOpen(false);
    } catch (error) {
      dispatch(showNotification({ message: error || t("country.msg.failedUpdate"), type: "error" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-md w-full max-w-lg">
        <h2 className="text-xl font-bold mb-4">{t("country.modal.edit")}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Country flag selection dropdown */}
          <div>
            <label className="block mb-1 font-medium">{t("country.field.selectCountry")}</label>
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleCountrySelect}
              className="w-full border px-3 py-2 rounded-md"
              disabled={loadingCountries}
            >
              {loadingCountries ? (
                <option value="">{t("country.field.loadingCountries")}</option>
              ) : (
                <>
                  <option value="">{t("country.field.selectPlaceholder")}</option>
                  {countriesList.map((c) => (
                    <option key={c.cca2} value={c.cca2}>
                      {c.name.common}
                    </option>
                  ))}
                </>
              )}
            </select>
            {loadingCountries && (
              <p className="text-sm text-gray-500 mt-1">{t("country.field.fetchingApi")}</p>
            )}
          </div>
          {/* Display the selected flag image and country name */}
          {formData.countryFlagImage && (
            <div className="flex items-center space-x-2">
              <img src={formData.countryFlagImage} alt={formData.countryName} className="w-12 h-8" />
              <span>{formData.countryName}</span>
            </div>
          )}
          {/* Allow manual editing as well */}
          <input
            type="text"
            name="countryFlagImage"
            placeholder={t("country.field.flagImage")}
            value={formData.countryFlagImage}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="countryCode"
            placeholder={t("country.field.code")}
            value={formData.countryCode}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="countryName"
            placeholder={t("country.field.name")}
            value={formData.countryName}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="currency"
            placeholder={t("country.field.currency")}
            value={formData.currency}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="localCurrencySymbol"
            placeholder={t("country.field.localSymbol")}
            value={formData.localCurrencySymbol}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
          />
          <input
            type="text"
            name="globalCurrencySymbol"
            placeholder={t("country.field.globalSymbol")}
            value={formData.globalCurrencySymbol}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
          />
          <input
            type="text"
            name="defaultLanguage"
            placeholder={t("country.field.defaultLanguage")}
            value={formData.defaultLanguage}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="number"
            step="0.01"
            name="taxRate"
            placeholder={t("country.field.taxRate")}
            value={formData.taxRate}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="timezone"
            placeholder={t("country.field.timezone")}
            value={formData.timezone}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <input
            type="text"
            name="mobileCountryCode"
            placeholder={t("country.field.mobileCountryCode")}
            value={formData.mobileCountryCode}
            onChange={handleChange}
            className="w-full border px-3 py-2 rounded-md"
            required
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="mr-2"
            />
            <label>{t("country.field.active")}</label>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button type="button" onClick={() => setIsOpen(false)} className="px-4 py-2 border rounded-md">
              {t("country.button.cancel")}
            </button>
            <button type="submit" className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md">
              {t("country.button.update")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCountryModal;
